'use client'

import React from 'react'
import Link from 'next/link'
import BlogPostLayout from '@/app/components/BlogPostLayout'
import { BlogTLDR, BlogKeyTakeaways, BlogFAQ, BlogSources, BlogLastUpdated, BlogComparisonTable, BlogAppCTA } from '@/app/components/blog/BlogAeo'
import RiskQuizInlineNudge from '@/app/components/blog/RiskQuizInlineNudge'
import InlineGuessReveal from '@/app/components/blog/InlineGuessReveal'
import KeyStats from '@/app/components/blog/KeyStats'

export default function PostContent() {
  return (
    <BlogPostLayout
      title="PFAS in Cooking Oil: When the Plastic Doesn't Come From the Bottle"
      author="MicroPlastics Research Desk"
      date="August 17, 2026"
      readTime="12 min read"
      heroImage="/blog/heroes/food.webp"
      heroImageAlt="PFAS in cooking oil: PTFE particles found in olive oil point at production-line equipment rather than the squeeze bottle, and fat extracts more from plastic than water does"
    >
      <BlogLastUpdated iso="2026-08-17" formatted="August 17, 2026" />

      <BlogTLDR>
        <strong>A third-party test of a popular olive oil found PTFE — the
        polymer in Teflon, and a PFAS — suspended in the oil. The squeeze
        bottle is not made of PTFE.</strong>{' '}
        That single mismatch is the most interesting thing in the consumer
        plastics conversation right now, because it points somewhere nobody
        looks: <strong>not at the packaging, but at the production line.</strong>{' '}
        PTFE is a standard material for gaskets, seals, hose linings and
        non-stick surfaces in food processing equipment. Meanwhile the
        peer-reviewed picture on PFAS in edible oils is real but modest —
        <strong>PFOA measured from 53 pg/g in olive oil up to 1,962 pg/g in
        sunflower oil</strong>, with some Italian olive and peanut oils below
        the detection limit entirely. And there is a physical reason oil is the
        worst case for any of this:{' '}
        <strong>oil is nearly 100% fat, and fat is the most effective solvent
        there is for pulling additives out of a polymer.</strong> Water barely
        touches plastic by comparison.
      </BlogTLDR>

      <BlogKeyTakeaways
        items={[
          <><strong>PTFE in oil cannot come from a PET or HDPE squeeze bottle.</strong> When the polymer found does not match the packaging, the contamination happened upstream.</>,
          <><strong>PTFE is a PFAS.</strong> A fluoropolymer — FDA-permitted for food contact, and simultaneously a member of the forever-chemicals family.</>,
          <><strong>Processing equipment is full of it:</strong> gaskets, valve seats, hose linings, pump seals and non-stick surfaces throughout filling and bottling lines.</>,
          <><strong>Fat is the aggressive medium.</strong> Migration testing uses fatty simulants for exactly this reason — oil extracts far more from plastic than water or acid does.</>,
          <><strong>Peer-reviewed PFAS levels in edible oils vary hugely by oil type</strong> — 53 pg/g PFOA in olive oil versus 1,962 pg/g in sunflower — and some samples show nothing at all.</>,
          <><strong>Phthalates are the better-documented problem</strong> in this category, with DEHP repeatedly detected in olive oils in independent testing.</>,
          <><strong>Glass and tin are the reliable fix for packaging</strong> — but neither addresses the equipment upstream, which is the part no consumer choice reaches.</>,
        ]}
      />

      <KeyStats
        heading="What has actually been measured"
        stats={[
          { value: '53 → 1,962', label: 'pg/g PFOA, olive oil to sunflower oil', context: 'the measured range across edible oil types; some olive and peanut oils were below detection entirely', source: 'Research progress of perfluoroalkyl substances in edible oil, Foods 12(13)', sourceUrl: 'https://www.mdpi.com/2304-8158/12/13/2624' },
          { value: '67', label: 'cooking oils sent for PFAS testing', context: 'olive, canola, avocado, coconut, vegetable and ghee, at an EPA-certified lab', source: 'Mamavation cooking oil PFAS testing', sourceUrl: 'https://mamavation.com/food/pfas-found-in-cooking-oils-non-toxic-guide.html' },
          { value: 'PTFE', label: 'polymer identified in an olive oil test', context: 'a fluoropolymer and a PFAS — and not the material the squeeze bottle is made from', source: 'Oasis Health third-party test, 2026', sourceUrl: 'https://x.com/oasishealthapp/status/2075002784328655188' },
          { value: '~100%', label: 'fat content of cooking oil', context: 'which is why fatty simulants represent the worst case in food-contact migration testing', source: 'EU food contact migration testing — fatty food simulants, Regulation (EU) No 10/2011', sourceUrl: 'https://eur-lex.europa.eu/legal-content/EN/TXT/?uri=CELEX%3A32011R0010' },
        ]}
      />

      <h2>The mismatch that matters</h2>
      <p>
        Most contamination stories are boring because the answer is obvious:
        plastic packaging, plastic in the product. This one is interesting
        precisely because the numbers do not line up that way.
      </p>
      <p>
        A third-party test of a widely sold extra virgin olive oil reported{' '}
        <strong>two microplastic particles per millilitre — one PTFE and one
        rubber</strong>, roughly thirty per tablespoon. The finding that matters
        is not the count. It is the identification.{' '}
        <strong>PTFE is not what squeeze bottles are made of.</strong> They are
        typically PET or HDPE. If PTFE is in the oil, it did not come from the
        container the oil was sold in.
      </p>
      <p>
        So where does PTFE live in the life of a bottle of olive oil? In the
        equipment.{' '}
        <strong>Fluoropolymers are standard throughout food processing</strong>{' '}
        — gasket material, valve seats, pump seals, the internal lining of
        transfer hoses, and non-stick surfaces on machinery that handles sticky
        or fatty product. Every one of those is a surface that fatty liquid
        passes over under pressure, at temperature, thousands of litres at a
        time.
      </p>
      <p>
        This is a single third-party test rather than peer-reviewed work, and we
        would not build a health claim on it. But as a{' '}
        <em>direction-finding</em> result it is genuinely useful, because it
        redirects attention from the thing consumers can see to the thing they
        cannot.
      </p>

      <InlineGuessReveal
        source="pfas_cooking_oil_ptfe"
        question="An olive oil test finds PTFE particles. The bottle is PET. Where did the PTFE come from?"
        options={[
          { label: 'The bottle, mislabelled' },
          { label: 'The cap liner' },
          { label: 'Processing equipment upstream', correct: true },
          { label: 'The olives themselves' },
        ]}
        answer="The equipment — gaskets, seals and hose linings"
        answerNote={
          <>
            PTFE is a workhorse material in food processing: valve seats, pump
            seals, gasket faces and the lining of transfer hoses, chosen
            because it is chemically inert and nothing sticks to it. Oil moves
            across those surfaces under pressure during pressing, filtering,
            transfer and filling. When the polymer identified in a product does
            not match its packaging, the packaging is not the story — and no
            amount of switching to glass will change it.
          </>
        }
        ctaText="Packaging is the part you can choose. Start there."
        ctaLabel="Scan a bottle"
      />

      <RiskQuizInlineNudge source="blog_inline_pfas_cooking_oil" />

      <h2>Why oil is the worst case for any plastic contact</h2>
      <p>
        There is a piece of regulatory science that explains this whole category
        in one sentence, and almost nobody outside food packaging knows it.
      </p>
      <p>
        When migration from food-contact plastic is tested, laboratories do not
        use the actual food. They use{' '}
        <strong>simulants</strong> chosen to represent worst cases: dilute
        acetic acid for acidic foods, ethanol for alcoholic ones, and{' '}
        <strong>olive oil or an equivalent fatty simulant for anything
        fatty.</strong> Fat is in the panel because it is the aggressive one.
        Non-polar additives — plasticisers, stabilisers, residual monomers —
        are far more soluble in fat than in water, so a fatty food pulls
        substances out of a polymer that water would leave alone entirely.
      </p>
      <p>
        Which means cooking oil is not an ordinary product in a plastic bottle.{' '}
        <strong>It is the test simulant, sitting in the packaging, for
        months.</strong> Anything extractable from that bottle has ideal
        conditions and unlimited time to be extracted. That is true regardless
        of brand, and it is the strongest argument in the category for glass or
        tin.
      </p>

      <BlogComparisonTable
        caption="Cooking oil packaging, ranked by what it can contribute (checked August 2026)"
        columns={[
          { key: 'p', label: 'Packaging' },
          { key: 'c', label: 'What it can add' },
          { key: 'v', label: 'Verdict' },
        ]}
        rows={[
          { p: 'Dark glass bottle', c: 'Nothing from the vessel; check the cap liner', v: 'Best available — and dark glass also protects against light oxidation' },
          { p: 'Tin / metal can', c: 'Depends entirely on the internal lining', v: 'Good, but the liner is a polymer and usually undisclosed' },
          { p: 'PET squeeze bottle', c: 'Antimony catalyst residue, plasticiser migration into fat', v: 'The fat-contact issue is real, and time in storage compounds it' },
          { p: 'HDPE jug', c: 'Lower migration than PET, but opaque plastic still in fat contact', v: 'Common for bulk; the same physics, more of it' },
          { p: 'Plastic-lined bag-in-box', c: 'Large polymer surface area against fat', v: 'Worst surface-to-volume ratio in the category' },
        ]}
      />

      <h2>The contaminant that is better documented than PFAS</h2>
      <p>
        If you are auditing your cooking oil, the evidence is actually stronger
        for a different family of chemicals.{' '}
        <strong>Phthalates — particularly DEHP — have been repeatedly detected
        in olive oils in independent laboratory testing</strong>, at
        concentrations in the parts-per-billion range. Phthalates are
        plasticisers, they are not chemically bound to the polymer, and they are
        highly fat-soluble. They are, in other words, exactly what you would
        predict would end up in an oil stored in plastic.
      </p>
      <p>
        PFAS in edible oils is real but more variable and generally lower.
        Peer-reviewed measurements put PFOA from{' '}
        <strong>53 pg/g in olive oil to 1,962 pg/g in sunflower oil</strong>,
        while separate testing of Italian olive and peanut oils found PFOA and
        PFOS below the limit of detection altogether. Two honest readings follow
        from that: the contamination is not universal, and the oil type matters
        more than the brand.
      </p>

      <BlogAppCTA
        headline="Audit what is already in your kitchen"
        sub="Cooking oil is one bottle. The pantry around it is fifty. Scan any product with MicroPlastics to see the packaging materials and get a 0–100 score."
      />

      <h2>What to actually do</h2>
      <ul>
        <li>
          <strong>Buy oil in dark glass or tin.</strong> This is the one lever
          you fully control, it removes the longest-duration fat-to-plastic
          contact in your kitchen, and dark glass helps with oxidation too.
        </li>
        <li>
          <strong>Decant if you buy plastic.</strong> If the format you like
          only comes in a squeeze bottle, move it into glass at home. Contact
          time is the variable that matters most.
        </li>
        <li>
          <strong>Do not store oil near the hob.</strong> Warmth accelerates
          both migration and oxidation, and the cupboard above the cooker is the
          hottest storage in most kitchens.
        </li>
        <li>
          <strong>Buy smaller bottles and turn them over faster.</strong> Months
          of storage is the mechanism; a bottle you finish in three weeks has
          far less of it.
        </li>
        <li>
          <strong>Do not switch brands on the strength of one test.</strong>{' '}
          A single third-party result identifies a direction, not a ranking, and
          no olive oil brand currently publishes routine microplastic testing —
          so any brand-by-brand league table you see is largely invented.
        </li>
        <li>
          <strong>Recognise the limit of consumer choice here.</strong> If PTFE
          is entering oil from processing equipment, packaging changes do not
          touch it. That is a supply-chain disclosure problem, and the honest
          answer is that it is not currently solvable from the shopping aisle.
        </li>
      </ul>

      <p>
        Related:{' '}
        <Link href="/blog/does-graza-have-microplastics" className="text-forest underline">does Graza olive oil have microplastics</Link>,{' '}
        <Link href="/blog/olive-oil-brands-ranked-microplastics" className="text-forest underline">olive oil brands ranked</Link>,{' '}
        <Link href="/blog/pfas-forever-chemicals-food-water-explained" className="text-forest underline">PFAS and forever chemicals explained</Link>, and{' '}
        <Link href="/blog/microplastics-vs-pfas-vs-bpa" className="text-forest underline">microplastics vs PFAS vs BPA</Link>.
      </p>

      <BlogFAQ
        items={[
          {
            question: 'Is there PFAS in cooking oil?',
            answer: 'Sometimes, at low and highly variable levels. Peer-reviewed measurements of edible oils put PFOA between roughly 53 pg/g in olive oil and 1,962 pg/g in sunflower oil, while separate testing of Italian olive and peanut oils found PFOA and PFOS below the detection limit entirely. Oil type appears to matter more than brand. A separate route exists via PTFE, a fluoropolymer and therefore a PFAS, which is widely used in food processing equipment as gasket, seal and hose-lining material.',
          },
          {
            question: 'Why would PTFE be in olive oil if the bottle is not made of PTFE?',
            answer: 'Because the bottle is not the only plastic the oil touches. PTFE is a standard material in food processing equipment — valve seats, pump seals, gasket faces and the internal lining of transfer hoses — chosen precisely because it is inert and non-stick. Oil passes over those surfaces under pressure during pressing, filtration, transfer and filling. When the polymer identified in a product does not match its packaging, the contamination happened upstream of the bottle, and switching to glass will not address it.',
          },
          {
            question: 'Does olive oil pull more chemicals out of plastic than water does?',
            answer: 'Yes, substantially, and this is established regulatory science rather than speculation. Food-contact migration testing uses standardised simulants for worst cases, and olive oil or an equivalent fatty simulant represents fatty foods specifically because fat is the most aggressive medium. Plasticisers, stabilisers and residual monomers are non-polar and far more soluble in fat than in water, so an oil in a plastic bottle experiences ideal extraction conditions for the entire time it sits on the shelf.',
          },
          {
            question: 'Is olive oil in a plastic squeeze bottle bad?',
            answer: 'It is the least favourable common format, for a physical reason rather than a brand one: a fatty product in prolonged contact with a polymer is the worst-case combination for migration. Dark glass or tin removes the packaging variable entirely, and dark glass additionally protects against light-driven oxidation. If you prefer a squeeze format, decanting into glass at home addresses most of it, since contact time is the dominant factor.',
          },
          {
            question: 'Are phthalates or PFAS the bigger issue in cooking oil?',
            answer: 'Phthalates are the better-documented concern in this category. Independent laboratory testing has repeatedly detected phthalates, particularly DEHP, in olive oils at parts-per-billion concentrations. Phthalates are plasticisers that are not chemically bound to the polymer and are highly fat-soluble, making them exactly what you would expect to find in oil stored in plastic. PFAS levels in edible oils are real but generally lower and far more variable between oil types.',
          },
          {
            question: 'Which cooking oil brand is safest for microplastics?',
            answer: 'No brand-level ranking is currently defensible, because no olive oil brand publishes routine microplastic testing. Independent one-off tests exist and are useful for direction, but a league table built from scattered single results would be misleading. The reliable decisions are format rather than brand: choose dark glass or tin over plastic, buy quantities you will use quickly, and store away from heat.',
          },
        ]}
      />

      <BlogSources
        items={[
          { authors: 'Wang Y, et al.', year: 2023, title: 'Research progress of perfluoroalkyl substances in edible oil — PFOA measured from 53 pg/g in olive oil to 1,962 pg/g in sunflower oil; Italian olive and peanut oils below detection limit', publisher: 'Foods 12(13):2624', url: 'https://www.mdpi.com/2304-8158/12/13/2624' },
          { authors: 'Sadowska-Rociek A, et al.', year: 2020, title: 'Determination of perfluoroalkyl substances (PFASs) in fats and oils by QuEChERS/micro-HPLC-MS/MS', publisher: 'Food Research International', url: 'https://www.sciencedirect.com/science/article/abs/pii/S0963996920306086' },
          { authors: 'Oasis Health', year: 2026, title: 'Third-party test of Graza extra virgin olive oil: 2 microplastic particles per millilitre, identified as one PTFE and one rubber — PTFE is not the squeeze bottle material. Single independent test, not peer-reviewed', publisher: 'Oasis Health', url: 'https://x.com/oasishealthapp/status/2075002784328655188' },
          { authors: 'Mamavation', year: 2026, title: 'Cooking oils and olive oils tested for PFAS and phthalates at an EPA-certified laboratory — 67 oils across olive, canola, avocado, coconut, vegetable and ghee', publisher: 'Mamavation', url: 'https://mamavation.com/food/olive-oils-tested-for-toxic-phthalates-buying-guide.html' },
        ]}
      />
    </BlogPostLayout>
  )
}
